import * as React from "react";
import PageHeaderView from "../../body/PageHeaderView";
import PageContentView from "../../body/PageContentView";
import {none, option, Option} from "ts-option";
import Loading from "../../ui/loading/Loading";
import Toast from "../../helper/Toast";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import DictionaryItem from "../../../model/dictionary/DictionaryItem";
import CreateDictionarySortableItemModal from "./modal/CreateDictionarySortableItemModal";
import EditDictionarySortableItemModal from "./modal/EditDictionarySortableItemModal";
import DictionarySortableItemsTable from "./DictionarySortableItemsTable";
import ButtonGroupWrapper from "../../../hiberUiKit/src/atoms/Buttons/ButtonGroupWrapper";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";

interface Props {
	/**
	 * Dictionary title
	 */
	title: string;
	/**
	 * Already existing items of dictionary
	 */
	items: DictionaryItem[]
	/**
	 * Sort order update method
	 * @param {DictionaryItem[]} items
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateSortOrder: (items: DictionaryItem[], onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Create item method
	 * @param {DictionaryItem} item
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 * @returns {any}
	 */
	createItem: (item: DictionaryItem, onSuccess: OnSuccess, onFailure: OnFailure) => any;
	/**
	 * Update item method
	 * @param {string} itemId
	 * @param {DictionaryItem} item
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateItem: (itemId: string, item: DictionaryItem, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Filters renderer
	 * @returns {JSX.Element}
	 */
	renderFilters?: () => JSX.Element
	/**
	 * Header renderer
	 * @returns {JSX.Element}
	 */
	renderHeaderBody?: () => JSX.Element
	/**
	 * Row body renderer
	 * @param {DictionaryItem} item
	 * @param {number} index
	 * @returns {JSX.Element}
	 */
	renderRowBody?: (item: DictionaryItem, index: number) => JSX.Element
	/**
	 * Error handler
	 * @param err
	 * @returns {string | null}
	 */
	errorHandler?: (err: any) => string | null
}

interface IComponentState {
	/**
	 * Dictionary item create modal visibility flag
	 */
	createItemModalVisible: boolean;
	/**
	 * Dictionary item to edit
	 */
	item: Option<DictionaryItem>;
	/**
	 * Dictionary items with new sort order.
	 */
	items: DictionaryItem[];
	/**
	 * Indicates if sort order has changed.
	 */
	isDirty: boolean;
}


/**
 * Dictionary Items list component.
 */
export default class DictionarySortableItemsList extends React.Component<Props, IComponentState> {

	constructor(props: Props) {
		super(props);
		this.state = {
			createItemModalVisible: false,
			item: none,
			items: props.items,
			isDirty: false
		};
		this.closeModal = this.closeModal.bind(this);
		this.openModal = this.openModal.bind(this);
		this.onSubmitOrder = this.onSubmitOrder.bind(this);
		this.onOrderChange = this.onOrderChange.bind(this);
	}

	componentWillReceiveProps(nextProps: Props) {
		this.setState({items: nextProps.items})
	}

	render() {
		return (
			<div>
				<PageHeaderView header={this.props.title}>
					<ButtonGroupWrapper>
						<ButtonPrimary onClick={this.openModal}
									   label={`Create ${this.props.title}`}
									   variant={"burgundy"}/>
						<ButtonPrimary onClick={() => this.onSubmitOrder(this.state.items)}
									   disabled={this.props.items.length == 0 || !this.state.isDirty}
									   label={"Save Changes"}
									   variant={"burgundy"}/>
					</ButtonGroupWrapper>
					{this.props.renderFilters && this.props.renderFilters()}
				</PageHeaderView>
				<PageContentView>
					<Loading isLoading={this.state.items.length == 0}>
						<DictionarySortableItemsTable items={this.props.items}
													  onRowClick={(channel) => {
														  this.setState({item: option(channel)})
													  }}
													  onOrderChange={this.onOrderChange}
													  renderHeaderBody={this.props.renderHeaderBody}
													  renderRowBody={this.props.renderRowBody}/>
					</Loading>
					{this.renderCreateItemModal()}
					{this.renderEditItemModal()}
				</PageContentView>
			</div>
		)
	}

	/**
	 * Render modal.
	 */
	renderCreateItemModal() {
		if (this.state.createItemModalVisible) {
			return (
				<CreateDictionarySortableItemModal
					title={this.props.title}
					onCreateItem={this.props.createItem}
					onSuccess={this.closeModal}
					onCancel={this.closeModal}
					errorHandler={this.props.errorHandler}/>
			);
		}
	}

	/**
	 * Close modal with item creator.
	 */
	closeModal() {
		this.setState({createItemModalVisible: false});
	}

	/**
	 * Open modal with item creator.
	 */
	openModal() {
		this.setState({createItemModalVisible: true});
	}

	/**
	 * Render modal.
	 */
	renderEditItemModal() {
		if (this.state.item.isDefined) {
			return (
				<EditDictionarySortableItemModal title={`${this.props.title}`}
												 item={this.state.item.get}
												 onSuccess={() => this.setState({item: none})}
												 onCancel={() => this.setState({item: none})}
												 onUpdateItem={this.props.updateItem}
												 errorHandler={this.props.errorHandler}/>
			);
		}
	}

	/**
	 * Submit order change.
	 * @param {DictionaryItem[]} items
	 */
	onSubmitOrder(items: DictionaryItem[]) {
		this.props.updateSortOrder(
			items,
			() => {
				this.setState({isDirty: false},
					() => {
						Toast.info("Sort order has been updated.", "Update sort order");
					});
			},
			() => {
				Toast.error("Something went wrong. Please try again later.", "Update sort order")
			}
		);
	}

	/**
	 * Sort order change callback.
	 * @param {DictionaryItem[]} items
	 */
	onOrderChange(items: DictionaryItem[]) {
		this.setState({items: items, isDirty: true});
	}
}
